// Realistic, deterministic data behind the in-memory API (fake.ts): three companies, four
// digital employees, the recruiting DE catalog (13 §DE scorecard catalog), weekly values,
// costs, time entries, timeline events, ingest keys and Friday reports. Every number is a pure
// function of (DE, metric, week), so a screen and a test that asks the fake for the same
// payload always agree. Shapes are the SDK's; `fake-contract.test.ts` parses every response
// with the zod contract.
//
// Unit conventions follow the Friday report (services/api/src/reports/friday/format.ts):
// percent = fraction, currency = minor units, time = minutes, score 0–100+, attainment fraction.
import {
  attainment,
  DEFAULTS,
  type Contributor as EngineContributor,
  finalBand,
  type MetricScoreInput,
  metricHealth,
  propagate,
  type SeatScores,
  scoreOwnMetrics,
} from "@companyos/domain-kpi";
import { isoWeekKey } from "../focus/focus";
import { isoDate, shiftWeek, weekStart, weeksBetween } from "../focus/weeks";
import type {
  AgentKey,
  CompanyRow,
  Contributor,
  CostEvent,
  DeProfile,
  DeSummary,
  FridayReportRecord,
  Scorecard,
  ScorecardMetric,
  TimeEntry,
  TimelineItem,
} from "./types";

// Ids -----------------------------------------------------------------------------------------

const hex = (n: number, width: number) => n.toString(16).padStart(width, "0");
/** A valid v7-shaped UUID, stable per (group, n). */
export const uid = (group: number, n: number) =>
  `0193a0a0-${hex(group, 4)}-7000-8000-${hex(n, 12)}`;

/**
 * SHARED-DES (docs/design/2026-10-30-shared-des-and-fulfillment.md): Acme's shared DE, Account
 * Health, its home seat under the Fulfillment Lead, and the seats and teams it serves (or not).
 */
export const SHARED_DE = {
  de: uid(0x1000, 0xa03),
  deSeat: uid(0x2000, 0xa03),
  fulfillmentLead: uid(0x2000, 0xa21),
  fulfillmentHolder: uid(0x1000, 0xa21),
  ceo: uid(0x2000, 0xa20),
  salesperson: uid(0x2000, 0xa22),
  recruiter: uid(0x2000, 0xa23),
  // After the generated teams' ids (they sort first: "the top team" stays the seeded one).
  teamFulfillment: uid(0x4f50, 1),
  teamSales: uid(0x4f50, 2),
  teamRecruiting: uid(0x4f50, 3),
} as const;

/** The Fulfillment Lead's name (she holds a seat but is not on the seeded people roster). */
export const sharedDesName = (membershipId: string): string | null =>
  membershipId === SHARED_DE.fulfillmentHolder ? "Maria Ionescu" : null;

/**
 * A person's crew membership id (`{owner}'s agent`, M4-01): stable per (company, owner), so the
 * crews twin and the chat twin's agent rooms name the same crew.
 */
export function crewMembershipIdOf(companyId: string, ownerId: string): string {
  const n =
    (Number.parseInt(companyId.slice(-4), 16) % 4096) * 2 ** 32 +
    Number.parseInt(ownerId.slice(-8), 16);
  return uid(0xc7e0, n);
}

/** Agencies marked `cp.agency.platform = true` (HTVA): their admins are platform admins. */
export const PLATFORM_AGENCY_IDS: readonly string[] = ["0193a0a0-0000-7000-8000-00000000a9e1"];

export const COMPANY_IDS = {
  acme: "0193a0a0-0000-7000-8000-00000000ac01",
  beta: "0193a0a0-0000-7000-8000-00000000be02",
  gamma: "0193a0a0-0000-7000-8000-000000000a03",
  // S1-17: HTVA's own company (slug `htva`): the gate numbers, no DEs.
  htva: "0193a0a0-0000-7000-8000-00000000f0a1",
} as const;
export type CompanyKey = keyof typeof COMPANY_IDS;

/** DE membership ids (the `{id}` of `/c/{slug}/scorecard/de/{id}`). */
export const DE_IDS = {
  riley: uid(0x1000, 0xa01),
  sam: uid(0x1000, 0xa02),
  bea: uid(0x1000, 0xb01),
  gus: uid(0x1000, 0xc01),
} as const;

/**
 * SCREENS-NEEDS-YOU: Acme's seeded needs-you ask (Sam waits on the owner's LinkedIn login). The
 * screens twin serves it; the chat twin posts its `screen_ask:<id>` card in #recruiting.
 */
export const SCREEN_ASK_ID = uid(0x5e15, 1);

const EPOCH = "2026-06-01T09:00:00Z";

/** FNV-1a → [0, 1). Deterministic noise per key. */
function noise(key: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < key.length; i++) {
    h ^= key.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  // murmur3 fmix32: keys that differ in one digit must not land next to each other.
  h ^= h >>> 16;
  h = Math.imul(h, 0x85ebca6b);
  h ^= h >>> 13;
  h = Math.imul(h, 0xc2b2ae35);
  h ^= h >>> 16;
  return (h >>> 0) / 2 ** 32;
}

// Companies -----------------------------------------------------------------------------------

export type FakeCompany = CompanyRow;

function company(key: CompanyKey, name: string, timezone: string): FakeCompany {
  return {
    id: COMPANY_IDS[key],
    slug: key,
    name,
    kind: "operating",
    settings: {
      friday_report: { generate_at: "16:00", auto_send: false },
      // BYO-01: as the real seed opens them on its demo company: API keys and tool connections
      // (M4-04: and crew mail and calendar).
      ...(key === "acme"
        ? {
            flags: {
              byo_model_provider_keys: true,
              byo_tool_connections: true,
              crew_mail_calendar: true,
            },
          }
        : {}),
    },
    calendar_config: { timezone, weekStartDay: 1, fiscalYearStartMonth: 1 },
    reporting_currency: "USD",
    // MANAGED-POLICY: the seeded companies predate managed mode (the migration keeps them as
    // they were); a client an operator creates in the twin starts managed.
    agent_mode: "self_serve",
    created_at: EPOCH,
    updated_at: EPOCH,
  };
}

export function seedCompanies(): FakeCompany[] {
  return [
    company("acme", "Acme Recruiting", "America/New_York"),
    company("beta", "Beta Staffing", "America/Chicago"),
    company("gamma", "Gamma Search", "Europe/London"),
    company("htva", "HTVA", "America/New_York"),
  ];
}

// Catalog -------------------------------------------------------------------------------------

type MetricSpec = Pick<
  ScorecardMetric,
  "key" | "name" | "unit" | "direction" | "visibility" | "tracking_mode" | "kpi_weight"
> & {
  goal_weight: number;
  target: number | null;
  /** Typical weekly value for a DE at skill 1, and the spread around it. */
  base: number;
  spread: number;
};

export const CATALOG: readonly MetricSpec[] = [
  {
    key: "de.tasks_completed",
    name: "Work done",
    unit: "number",
    direction: "gte",
    visibility: "client",
    tracking_mode: "scored",
    kpi_weight: 1,
    goal_weight: 0,
    target: 25,
    base: 27,
    spread: 12,
  },
  {
    key: "de.approval_rate",
    name: "Supervisor approval rate",
    unit: "percent",
    direction: "gte",
    visibility: "client",
    tracking_mode: "scored",
    kpi_weight: 1,
    goal_weight: 0,
    target: 0.9,
    base: 0.91,
    spread: 0.14,
  },
  {
    key: "de.candidates_sourced",
    name: "Candidates sourced",
    unit: "number",
    direction: "gte",
    visibility: "client",
    tracking_mode: "scored",
    kpi_weight: 1,
    goal_weight: 0,
    target: 40,
    base: 42,
    spread: 18,
  },
  {
    key: "de.interviews_booked",
    name: "Interviews booked",
    unit: "number",
    direction: "gte",
    visibility: "client",
    tracking_mode: "scored",
    kpi_weight: 2,
    goal_weight: 1,
    target: 4,
    base: 4.2,
    spread: 4,
  },
  {
    key: "de.supervisor_hours",
    name: "Supervisor hours",
    unit: "time",
    direction: "lte",
    visibility: "client",
    tracking_mode: "scored",
    kpi_weight: 1,
    goal_weight: 0,
    target: 600,
    base: 0, // from the time entries
    spread: 0,
  },
  {
    key: "de.founder_hours",
    name: "Founder hours",
    unit: "time",
    direction: "lte",
    visibility: "agency",
    tracking_mode: "scored",
    kpi_weight: 1,
    goal_weight: 0,
    target: 300,
    base: 0, // from the time entries
    spread: 0,
  },
  {
    key: "de.activity_total",
    name: "Actions taken",
    unit: "number",
    direction: "gte",
    visibility: "client",
    tracking_mode: "context",
    kpi_weight: 0,
    goal_weight: 0,
    target: null,
    base: 160,
    spread: 70,
  },
  {
    key: "de.cost_to_serve",
    name: "Cost to serve",
    unit: "currency",
    direction: "lte",
    visibility: "agency",
    tracking_mode: "context",
    kpi_weight: 0,
    goal_weight: 0,
    target: null,
    base: 0, // from the cost ledger
    spread: 0,
  },
];

export function metricDefId(companyId: string, key: string): string {
  const i = CATALOG.findIndex((m) => m.key === key);
  return uid(0x3000 + (Number.parseInt(companyId.slice(-4), 16) % 0x0fff), i + 1);
}

// Digital employees ---------------------------------------------------------------------------

export type FakeDe = {
  company: CompanyKey;
  membership_id: string;
  display_name: string;
  seat_id: string;
  seat_title: string;
  supervisor: { seat_id: string; title: string; membership_id: string; name: string };
  founder_membership_id: string;
  template_key: string;
  instance: string;
  /** Weeks before the current week the DE started (week 0 = this week). */
  started_weeks_ago: number;
  status: DeSummary["status"];
  skill: number;
  keys: AgentKey[];
  /**
   * Created through the UI (DE create wizard): no generated history, and the fields below are
   * what the app set, not derived from the seed.
   */
  fresh?: boolean;
  runtime?: DeSummary["runtime_ref"];
  started_on?: string | null;
  litellm_key_alias?: string | null;
  provisioning_error?: string | null;
  /** WALLET-P1: why a paused DE is paused (default `manual`). */
  pause_reason?: DeSummary["pause_reason"];
  /** AGENT-MENTION: removed by a manager (retired, keys revoked, off the roster). */
  removed_at?: string | null;
  /** HELPERS-2: the stored person name (shown only while the company names DEs by person). */
  person_name?: string | null;
  /** WF-POLISH: role, goal, backstory, standing orders (absent: none set). */
  profile?: {
    role: string | null;
    goal: string | null;
    backstory: string | null;
    standing_orders: string | null;
  };
  /** DE-PROFILE-LIVE: the shipped default of `profile` (absent: none shipped). */
  profile_default?: {
    role: string | null;
    goal: string | null;
    backstory: string | null;
    standing_orders: string | null;
  };
  /** ADI-02: the deploy mirror of a DE deployed through AiGentPanel (absent: never deployed). */
  deploy?: {
    template_version_id: string;
    bundle_sha: string;
    deploy_ref: string;
    verification_state: "verified" | "unverified";
    /** DC-05: the verification override the deploy runs under (a pilot). */
    pilot?: { deploy_id: string; reason: string; pilot_until: string };
  };
  /** DE-WIZARD: the job set-up the wizard and the edit mode saved (absent: none yet). */
  job?: FakeDeJob;
  /** DE-WIZARD: the DE's newest deploy, as readiness reads it (set by the twin, fake.ts). */
  latestDeploy?: () => FakeLatestDeploy | null;
  /**
   * WF-FIX: a self-hosted DE (production: its lead profile's `overrides.self_hosted`), set by
   * the test hook `selfHost`; `inbox` is the `requested_at` (ISO) of each queued wake-up waiting
   * in its inbox (`wakeups.inbox`).
   */
  self_hosted?: { runtime: NonNullable<DeProfile["self_hosted"]>["runtime"]; inbox: string[] };
};

/** DE-WIZARD: `de_job` in the twin (KPIs live on the seat, post approval on its settings). */
export type FakeDeJob = {
  path: "done_for_you" | "self_serve";
  submitted_at: string | null;
  home_channel_id: string | null;
  reporting: { metric_def_id: string; duty: "daily" | "weekly" | "monthly" }[];
  version: number;
  updated_at: string;
};

export type FakeLatestDeploy = {
  state: "staged" | "approved" | "handed_off" | "confirmed" | "failed" | "rejected";
  needs_approval: boolean;
  reason: string | null;
};

/**
 * DE-WIZARD readiness (services/api domain/org/digital-employees.ts): retired or removed, paused
 * and active first; a DE in setup by its newest deploy, else by whether its set-up was
 * submitted on the done-for-you path.
 */
export function deReadiness(de: FakeDe): DeSummary["readiness"] {
  if (de.removed_at || de.status === "retired") return { state: "not_live", reason: "Retired." };
  if (de.status === "paused") return { state: "not_live", reason: "Paused." };
  if (de.status === "not_deployed") return { state: "not_live", reason: "Not deployed." };
  if (de.status === "active") return { state: "live", reason: null };
  const d = de.latestDeploy?.() ?? null;
  if (!d) {
    if (!de.job?.submitted_at) return { state: "draft", reason: "Set-up not finished." };
    return de.job.path === "done_for_you"
      ? {
          state: "awaiting_approval",
          reason: "HTVA sets it up: it stages the first deploy, then the owner approves it.",
        }
      : { state: "not_live", reason: "Not deployed yet." };
  }
  switch (d.state) {
    case "staged":
      return d.needs_approval
        ? {
            state: "awaiting_approval",
            reason: "The owner approves the first deploy (Setup › Deploys).",
          }
        : { state: "deploying", reason: "Handing off to AiGentPanel." };
    case "approved":
    case "handed_off":
      return { state: "deploying", reason: "Handing off to AiGentPanel." };
    case "confirmed":
      return { state: "not_live", reason: "Deployed; waiting for its model key." };
    case "failed":
      return { state: "not_live", reason: `The deploy failed: ${d.reason || "no reason given"}.` };
    case "rejected":
      return {
        state: "not_live",
        reason: `The deploy was rejected: ${d.reason || "no reason given"}.`,
      };
  }
}

function key(de: Omit<FakeDe, "keys">, n: number, revokedWeeksAgo: number | null): AgentKey {
  const created = new Date(weekStart(isoWeekKey(new Date())));
  created.setUTCDate(created.getUTCDate() - 7 * de.started_weeks_ago);
  const revoked =
    revokedWeeksAgo === null
      ? null
      : new Date(created.getTime() + 7 * 86_400_000 * Math.max(1, revokedWeeksAgo)).toISOString();
  const prefix = `cosk_in_${hex(Math.floor(noise(`${de.membership_id}:${n}`) * 0xffff), 4)}`;
  return {
    company_id: COMPANY_IDS[de.company],
    id: uid(0x4000, Number.parseInt(de.membership_id.slice(-3), 16) * 16 + n),
    kind: "ingest",
    membership_id: de.membership_id,
    seat_id: de.seat_id,
    key_prefix: prefix,
    scopes: ["ingest:activity", "ingest:outcome", "ingest:cost"],
    backfill: false,
    meter_namespaces: [],
    source: null,
    status: revoked ? "revoked" : "active",
    last_used_at: revoked ? null : new Date(Date.now() - 2 * 3_600_000).toISOString(),
    expires_at: null,
    revoked_at: revoked,
    actor_kind: "agency_user",
    actor_membership_id: null,
    created_at: created.toISOString(),
    updated_at: revoked ?? created.toISOString(),
  };
}

const SAM_PROFILE = {
  role: "Sources candidates for open recruiting roles.",
  goal: "Five qualified candidates per open role each week.",
  backstory: null,
  standing_orders: "Never contact a candidate without the recruiting lead's approval.",
};

export function seedDes(): FakeDe[] {
  const base: Omit<FakeDe, "keys">[] = [
    {
      company: "acme",
      membership_id: uid(0x1000, 0xa01),
      display_name: "Riley",
      seat_id: uid(0x2000, 0xa01),
      seat_title: "Recruiting coordinator (DE)",
      supervisor: {
        seat_id: uid(0x2000, 0xa11),
        title: "Recruiting lead",
        membership_id: uid(0x1000, 0xa11),
        name: "Jordan Lee",
      },
      founder_membership_id: uid(0x1000, 0xf01),
      template_key: "recruiting_de",
      instance: "hermes-eu-1",
      started_weeks_ago: 11,
      status: "active",
      skill: 1.04,
      deploy: {
        template_version_id: uid(0x7d00, 0xa01),
        bundle_sha: "3f9a1c0be27d4c58a6e1f0d2b4c6a8e0f1d3b5c7e9a2c4e6f8b0d2f4a6c8e0b2",
        deploy_ref: uid(0x7d10, 0xa01),
        verification_state: "verified",
      },
    },
    {
      company: "acme",
      membership_id: uid(0x1000, 0xa02),
      display_name: "Sam",
      seat_id: uid(0x2000, 0xa02),
      seat_title: "Sourcer (DE)",
      supervisor: {
        seat_id: uid(0x2000, 0xa11),
        title: "Recruiting lead",
        membership_id: uid(0x1000, 0xa11),
        name: "Jordan Lee",
      },
      founder_membership_id: uid(0x1000, 0xf01),
      template_key: "recruiting_de",
      instance: "hermes-eu-1",
      started_weeks_ago: 3,
      status: "active",
      skill: 0.84,
      // DE-PROFILE-LIVE: Sam's profile as shipped (its default), so the Overview can show an
      // edit as "Differs from default" (e2e/de-profile-live.spec.ts).
      profile: SAM_PROFILE,
      profile_default: SAM_PROFILE,
    },
    {
      // SHARED-DES: one DE whose home is under the Fulfillment Lead and which also serves Sales
      // and the CEO (fake-shared-des.ts adds the CEO, Sales and Recruiting seats and the links).
      company: "acme",
      membership_id: SHARED_DE.de,
      display_name: "Account Health",
      seat_id: SHARED_DE.deSeat,
      seat_title: "Account health (DE)",
      supervisor: {
        seat_id: SHARED_DE.fulfillmentLead,
        title: "Fulfillment Lead",
        membership_id: SHARED_DE.fulfillmentHolder,
        name: "Maria Ionescu",
      },
      founder_membership_id: uid(0x1000, 0xf01),
      template_key: "recruiting_de",
      instance: "hermes-eu-1",
      started_weeks_ago: 2,
      status: "active",
      skill: 0.92,
      // No generated runs, time or cost: the other DEs' seeded history stays as it was.
      fresh: true,
    },
    {
      company: "beta",
      membership_id: uid(0x1000, 0xb01),
      display_name: "Bea",
      seat_id: uid(0x2000, 0xb01),
      seat_title: "Recruiting coordinator (DE)",
      supervisor: {
        seat_id: uid(0x2000, 0xb11),
        title: "Office manager",
        membership_id: uid(0x1000, 0xb11),
        name: "Priya Shah",
      },
      founder_membership_id: uid(0x1000, 0xf02),
      template_key: "recruiting_de",
      instance: "hermes-us-1",
      started_weeks_ago: 9,
      status: "active",
      skill: 0.96,
    },
    {
      company: "gamma",
      membership_id: uid(0x1000, 0xc01),
      display_name: "Gus",
      seat_id: uid(0x2000, 0xc01),
      seat_title: "Candidate screener (DE)",
      supervisor: {
        seat_id: uid(0x2000, 0xc11),
        title: "Head of talent",
        membership_id: uid(0x1000, 0xc11),
        name: "Tom Hughes",
      },
      founder_membership_id: uid(0x1000, 0xf03),
      template_key: "recruiting_de",
      instance: "hermes-eu-2",
      started_weeks_ago: 6,
      status: "paused",
      skill: 0.9,
    },
  ];
  return base.map((de) => ({
    ...de,
    keys: de.display_name === "Riley" ? [key(de, 1, 4), key(de, 2, null)] : [key(de, 1, null)],
  }));
}

export function deStartedOn(de: FakeDe, today: string): string {
  return isoDate(weekStart(shiftWeek(today, -de.started_weeks_ago)));
}

// Weekly values -------------------------------------------------------------------------------

/** `none`: before the DE started or after today; `partial`: the current week. */
function weekPhase(de: FakeDe, week: string, today: string): "none" | "partial" | "full" {
  const age = weeksBetween(week, today); // 0 = this week, 1 = last week
  if (age < 0 || age > de.started_weeks_ago) return "none";
  return age === 0 ? "partial" : "full";
}

/** Minutes of supervision and founder time logged for a DE in a week. */
export function weekMinutes(
  de: FakeDe,
  week: string,
  today: string,
): { supervision: number; founder: number; setup: number } {
  const phase = weekPhase(de, week, today);
  if (phase === "none") return { supervision: 0, founder: 0, setup: 0 };
  const age = weeksBetween(week, today);
  const ramp = Math.max(0, de.started_weeks_ago - age); // weeks since start
  const r = noise(`${de.membership_id}:${week}:min`);
  const scale = phase === "partial" ? 0.6 : 1;
  const supervision = Math.round(((ramp < 2 ? 520 : 330) + r * 260) * scale);
  const founder = Math.round(((ramp < 2 ? 260 : 90) + r * 120) * scale);
  const setup = ramp === 0 ? 240 : 0;
  return { supervision: supervision - (supervision % 5), founder: founder - (founder % 5), setup };
}

function round(v: number, unit: ScorecardMetric["unit"]): number {
  if (unit === "percent") return Math.round(Math.min(1, Math.max(0, v)) * 100) / 100;
  return Math.max(0, Math.round(v));
}

/** The DE's actual for a metric in a week, or null when there is nothing to show. */
export function actualFor(
  de: FakeDe,
  spec: MetricSpec,
  week: string,
  today: string,
): number | null {
  const phase = weekPhase(de, week, today);
  if (phase === "none") return null;
  if (spec.key === "de.supervisor_hours") return weekMinutes(de, week, today).supervision;
  if (spec.key === "de.founder_hours") return weekMinutes(de, week, today).founder;
  if (spec.key === "de.cost_to_serve") return weekCostTotal(de, week, today);
  const r = noise(`${de.membership_id}:${spec.key}:${week}`);
  const ramp = Math.min(1, (de.started_weeks_ago - weeksBetween(week, today) + 1) / 3);
  const level = spec.unit === "percent" ? 0.75 + 0.25 * ramp : 0.55 + 0.45 * ramp;
  const raw =
    (spec.base * de.skill * level + (r - 0.5) * spec.spread) *
    // A week in progress has fewer counts so far; a rate is a rate.
    (phase === "partial" && spec.unit !== "percent" ? 0.62 : 1);
  return round(raw, spec.unit);
}

// Scoring: the real domain-kpi engine (what S1-10 computes on read), so the fake's bands,
// scores, completeness and contributors follow the engine's rules, not an imitation of them:
// KPI score = weighted share of metrics met, goal score = weighted attainment, completeness =
// mean metric completeness, band grey below the 0.6 cutoff, contributors = goal weight ×
// shortfall in score points, biggest drag first.

const POLICY = DEFAULTS;
const CAP = POLICY.attainment_cap;

const r1 = (v: number) => Math.round(v * 10) / 10;
const r3 = (v: number) => Math.round(v * 1000) / 1000;

export type Viewer = {
  operator: boolean;
  /** HELPERS-2: the company names DEs by person (`settings.workforce.de_names`). */
  personNames?: boolean;
};

function visibleTo(viewer: Viewer, visibility: "client" | "agency"): boolean {
  return visibility === "client" || viewer.operator;
}

export type ScoreInput = {
  de: FakeDe;
  week: string;
  today: string;
  viewer: Viewer;
  currency: FakeCompany["reporting_currency"];
};

function periodOf(week: string): Scorecard["period"] {
  const start = weekStart(week);
  const end = new Date(start.getTime() + 7 * 86_400_000 - 1000);
  return {
    type: "week",
    key: week,
    starts_at: start.toISOString(),
    ends_at: `${end.toISOString().slice(0, 19)}Z`,
  };
}

function engineInput(m: ScorecardMetric): MetricScoreInput {
  return {
    metricId: m.metric_def_id,
    name: m.name,
    direction: m.direction,
    kpiWeight: m.tracking_mode === "scored" ? m.kpi_weight : 0,
    goalWeight: m.tracking_mode === "scored" ? m.goal_weight : 0,
    actual: m.actual,
    target: m.tracking_mode === "scored" ? m.target : null,
    targetMax: m.target_max,
    expected: m.expected,
    completeness: m.completeness,
  };
}

function contributorsOf(c: EngineContributor[]): Contributor[] {
  return c.map((x) => ({ ...x, shortfall: r1(x.shortfall), impact: r1(x.impact) }));
}

/**
 * A DE seat's scorecard for a week, for the viewer: agency rows go to operators only, and the
 * score is computed over the rows the viewer receives (a score never leaks a hidden metric).
 */
export function seatScorecard(input: ScoreInput): Scorecard {
  const { de, week, today, viewer } = input;
  const phase = weekPhase(de, week, today);
  const trendWeeks = Array.from({ length: 13 }, (_, i) => shiftWeek(week, i - 12));
  const metrics: ScorecardMetric[] = CATALOG.filter((s) => visibleTo(viewer, s.visibility)).map(
    (spec) => {
      const actual = actualFor(de, spec, week, today);
      const scored = spec.tracking_mode === "scored" && spec.target !== null;
      const att =
        scored && actual !== null && spec.target !== null
          ? attainment(spec.direction, actual, spec.target, CAP, null)
          : null;
      const expected = 5;
      // A week in progress has 3 of 5 working days in; a week with no data has none.
      const present = phase === "none" ? 0 : phase === "partial" ? 3 : 5;
      const state: ScorecardMetric["state"] =
        spec.tracking_mode === "context"
          ? "context"
          : actual === null
            ? "missing"
            : phase === "partial"
              ? "provisional"
              : "complete";
      return {
        metric_def_id: metricDefId(COMPANY_IDS[de.company], spec.key),
        key: spec.key,
        name: spec.name,
        unit: spec.unit,
        direction: spec.direction,
        visibility: spec.visibility,
        actual,
        target: spec.target,
        target_max: null,
        attainment: att === null ? null : r3(att),
        bands: null,
        health: metricHealth(att === null ? null : r3(att)),
        expected,
        present,
        completeness: present / expected,
        tracking_mode: spec.tracking_mode,
        state,
        kpi_weight: spec.kpi_weight,
        goal_weight: spec.goal_weight,
        trend: trendWeeks.map((w) => ({
          period_key: w,
          actual: actualFor(de, spec, w, today),
          target: spec.target,
        })),
      };
    },
  );

  const inputs = metrics.map(engineInput);
  const own = scoreOwnMetrics(inputs, CAP);
  const health = propagate(own, inputs, POLICY.own_metrics_weight, [], CAP);
  const completeness = r3(health.completeness);
  const kpi = health.kpiScore === null ? null : r1(health.kpiScore);
  const band = finalBand(kpi, completeness, POLICY.insufficient_data_cutoff, POLICY.kpi_thresholds);

  const minutes = weekMinutes(de, week, today);
  const byKind = weekCostByKind(de, week, today, viewer);
  return {
    subject: { kind: "seat", id: de.seat_id, name: de.seat_title },
    period: periodOf(week),
    computed_at: new Date().toISOString(),
    kpi_score: kpi,
    goal_score: health.goalScore === null ? null : r1(health.goalScore),
    completeness,
    band,
    metrics,
    contributors: contributorsOf(health.contributors),
    cost:
      phase === "none"
        ? null
        : {
            total_minor: Object.values(byKind).reduce((s, v) => s + v, 0),
            currency: input.currency,
            by_kind: byKind,
          },
    hours:
      phase === "none"
        ? null
        : {
            supervision_minutes: minutes.supervision,
            founder_minutes: viewer.operator ? minutes.founder : null,
          },
  };
}

/**
 * Company roll-up: children are the roll-up seats (the DE seats and the scored person seats,
 * equal weight), propagated by the engine; the company's own rows sum
 * (numbers) or average (percent) the seats' rows for display.
 */
export function companyScorecard(
  company: FakeCompany,
  cards: { card: Scorecard }[],
  week: string,
): Scorecard {
  const children = cards.map(({ card }) => ({
    subject: card.subject,
    kpi_score: card.kpi_score,
    goal_score: card.goal_score,
    completeness: card.completeness,
    band: card.band,
    weight: 1,
    metrics: card.metrics,
  }));
  const none: SeatScores = { kpiScore: null, goalScore: null, completeness: 1, scored: 0 };
  const health = propagate(
    none,
    [],
    POLICY.own_metrics_weight,
    children.map((c) => ({
      seatId: c.subject.id,
      name: c.subject.name,
      weight: c.weight,
      kpiScore: c.kpi_score,
      goalScore: c.goal_score,
      completeness: c.completeness,
    })),
    CAP,
  );
  const kpi = health.kpiScore === null ? null : r1(health.kpiScore);
  const completeness = r3(health.completeness);

  const first = cards[0]?.card;
  const combine = (values: (number | null)[], unit: string): number | null => {
    const present = values.filter((v): v is number => v !== null);
    if (!present.length) return null;
    const sum = present.reduce((s, v) => s + v, 0);
    return unit === "percent" ? Math.round((sum / present.length) * 100) / 100 : sum;
  };
  const metrics: ScorecardMetric[] = (first?.metrics ?? []).map((m) => {
    const rows = cards.map(({ card }) => card.metrics.find((x) => x.key === m.key));
    const byWeek = (key: string) =>
      combine(
        rows.map((r) => r?.trend.find((p) => p.period_key === key)?.actual ?? null),
        m.unit,
      );
    const actual = combine(
      rows.map((r) => r?.actual ?? null),
      m.unit,
    );
    const target =
      m.target === null ? null : m.unit === "percent" ? m.target : m.target * cards.length;
    const att =
      actual === null || target === null || m.tracking_mode !== "scored"
        ? null
        : attainment(m.direction, actual, target, CAP, null);
    return {
      ...m,
      actual,
      target,
      attainment: att === null ? null : r3(att),
      health: metricHealth(att === null ? null : r3(att), m.bands),
      present: Math.max(...rows.map((r) => r?.present ?? 0)),
      completeness: Math.max(...rows.map((r) => r?.completeness ?? 0)),
      state: actual === null && m.tracking_mode !== "context" ? "missing" : m.state,
      trend: m.trend.map((p) => ({
        period_key: p.period_key,
        actual: byWeek(p.period_key),
        target,
      })),
    };
  });

  const costs = cards.map((c) => c.card.cost).filter((c) => c !== null);
  const byKind: Record<string, number> = {};
  for (const c of costs)
    for (const [k, v] of Object.entries(c.by_kind)) byKind[k] = (byKind[k] ?? 0) + v;
  const hours = cards.map((c) => c.card.hours).filter((h) => h !== null);

  return {
    subject: { kind: "company", id: company.id, name: company.name },
    period: periodOf(week),
    computed_at: new Date().toISOString(),
    kpi_score: kpi,
    goal_score: health.goalScore === null ? null : r1(health.goalScore),
    completeness,
    band: finalBand(kpi, completeness, POLICY.insufficient_data_cutoff, POLICY.kpi_thresholds),
    metrics,
    contributors: contributorsOf(health.contributors),
    children,
    cost: costs.length
      ? {
          total_minor: costs.reduce((s, c) => s + c.total_minor, 0),
          currency: company.reporting_currency,
          by_kind: byKind,
        }
      : null,
    hours: hours.length
      ? {
          supervision_minutes: hours.reduce((s, h) => s + h.supervision_minutes, 0),
          founder_minutes: hours.every((h) => h.founder_minutes === null)
            ? null
            : hours.reduce((s, h) => s + (h.founder_minutes ?? 0), 0),
        }
      : null,
  };
}

// Cost ledger ---------------------------------------------------------------------------------

const SUBSCRIPTION_MINOR = 150_000; // $1,500 / month

function costRow(
  de: FakeDe,
  n: number,
  kind: CostEvent["kind"],
  amount: number,
  occurredAt: string,
  visibility: CostEvent["visibility"],
  meter: CostEvent["meter"],
  meterIdentity: string,
): CostEvent {
  return {
    company_id: COMPANY_IDS[de.company],
    occurred_at: occurredAt,
    id: uid(0x5000, Number.parseInt(de.membership_id.slice(-3), 16) * 4096 + n),
    period_key: occurredAt.slice(0, 7),
    kind,
    amount_minor: amount,
    currency: "USD",
    amount_micros: kind === "agent_tokens" ? amount * 10_000 : null,
    seat_id: de.seat_id,
    holder_membership_id: de.membership_id,
    meter_identity: meterIdentity,
    meter,
    source_ref:
      kind === "de_subscription"
        ? { system: "companyos", id: `sub-${de.display_name.toLowerCase()}` }
        : null,
    reverses_cost_event_id: null,
    price_list_ref: kind === "de_subscription" ? "pl-2026-10" : null,
    visibility,
    actor_kind: "system",
    actor_membership_id: null,
    created_at: occurredAt,
  };
}

/** Every cost event of a DE from its start to today (weekly usage, monthly subscription). */
export function costLedger(de: FakeDe, today: string): CostEvent[] {
  const out: CostEvent[] = [];
  const alias = `cos-${de.company}-${de.display_name.toLowerCase()}`;
  const months = new Set<string>();
  const startDay = deStartedOn(de, today);
  for (let age = de.started_weeks_ago; age >= 0; age--) {
    const week = shiftWeek(today, -age);
    const monday = weekStart(week);
    const friday = new Date(monday.getTime() + 4 * 86_400_000 + 17 * 3_600_000);
    const at = (
      age === 0 ? new Date(Math.min(friday.getTime(), Date.now() - 3_600_000)) : friday
    ).toISOString();
    const r = noise(`${de.membership_id}:${week}:cost`);
    const scale = age === 0 ? 0.6 : 1;
    const n = (de.started_weeks_ago - age) * 8;
    const month = isoDate(monday).slice(0, 7);
    if (!months.has(month)) {
      months.add(month);
      // Billed on the 1st, or on the start day in the first month.
      const day = `${month}-01` < startDay ? startDay : `${month}-01`;
      out.push(
        costRow(
          de,
          n,
          "de_subscription",
          SUBSCRIPTION_MINOR,
          `${day}T00:00:00Z`,
          "client",
          null,
          `companyos:subscription:${alias}-${month}`,
        ),
      );
    }
    out.push(
      costRow(
        de,
        n + 1,
        "de_usage",
        Math.round((3200 + r * 2600) * scale),
        at,
        "client",
        {
          model: "claude-sonnet",
          tokens_in: Math.round(2_400_000 * (0.7 + r)),
          tokens_out: Math.round(310_000 * (0.7 + r)),
        },
        `litellm:${alias}:wk-${week}`,
      ),
      costRow(
        de,
        n + 2,
        "agent_tokens",
        Math.round((1400 + r * 900) * scale),
        at,
        "agency",
        {
          model: "claude-haiku",
          tokens_in: Math.round(900_000 * (0.6 + r)),
          tokens_out: Math.round(80_000 * (0.6 + r)),
        },
        `litellm:${alias}-internal:wk-${week}`,
      ),
      costRow(
        de,
        n + 3,
        "agent_compute",
        Math.round(420 * scale),
        at,
        "agency",
        { minutes: Math.round(1800 * scale) },
        `hermes:${de.instance}:wk-${week}`,
      ),
    );
  }
  return out.sort((a, b) => b.occurred_at.localeCompare(a.occurred_at));
}

export function weekOf(iso: string): string {
  const d = new Date(iso);
  return isoWeekKey(new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
}

/** Weekly cost by kind for the viewer: that week's usage plus the subscription pro rata. */
export function weekCostByKind(
  de: FakeDe,
  week: string,
  today: string,
  viewer: Viewer,
): Record<string, number> {
  const rows = costLedger(de, today).filter(
    (e) =>
      e.kind !== "de_subscription" &&
      weekOf(e.occurred_at) === week &&
      visibleTo(viewer, e.visibility),
  );
  const out: Record<string, number> = {};
  if (rows.length) out.de_subscription = Math.round((SUBSCRIPTION_MINOR * 12) / 52);
  for (const e of rows) out[e.kind] = (out[e.kind] ?? 0) + e.amount_minor;
  return out;
}

/** All visibilities: the agency's cost to serve (`de.cost_to_serve`). */
function weekCostTotal(de: FakeDe, week: string, today: string): number {
  return Object.values(weekCostByKind(de, week, today, { operator: true })).reduce(
    (s, v) => s + v,
    0,
  );
}

// Time entries --------------------------------------------------------------------------------

export function timeEntries(de: FakeDe, week: string, today: string): TimeEntry[] {
  const m = weekMinutes(de, week, today);
  const monday = weekStart(week);
  const seq =
    Number.parseInt(de.membership_id.slice(-3), 16) * 1024 + weeksBetween("2026-W01", week) * 8;
  const row = (
    n: number,
    kind: TimeEntry["kind"],
    membership: string,
    minutes: number,
    dayOffset: number,
    visibility: TimeEntry["visibility"],
    note: string | null,
    approval: TimeEntry["approval_status"] = "approved",
  ): TimeEntry => {
    const day = new Date(monday.getTime() + dayOffset * 86_400_000);
    return {
      task_id: null,
      company_id: COMPANY_IDS[de.company],
      id: uid(0x6000, seq + n),
      seat_id: de.seat_id,
      membership_id: membership,
      kind,
      grain: "day",
      work_date: isoDate(day),
      period_key: week,
      started_at: null,
      ended_at: null,
      duration_seconds: minutes * 60,
      minutes,
      source: kind === "founder" ? "manual" : "workpulse",
      source_event_id: `fake:${de.display_name.toLowerCase()}:${week}:${n}`,
      revision: 1,
      supersedes_id: null,
      superseded_by: null,
      voided_at: null,
      approval_status: approval,
      decided_by_membership_id: approval === "approved" ? de.supervisor.membership_id : null,
      decided_at: approval === "approved" ? day.toISOString() : null,
      rejection_reason: null,
      source_ref: null,
      source_actor_ref: null,
      dims: null,
      stats: kind === "supervision" ? { activity_pct: 72 } : null,
      visibility,
      note,
      actor_kind: "person",
      actor_membership_id: membership,
      created_at: day.toISOString(),
    };
  };
  const out: TimeEntry[] = [];
  if (m.supervision) {
    const a = Math.round(m.supervision * 0.55);
    out.push(
      row(
        1,
        "supervision",
        de.supervisor.membership_id,
        a,
        0,
        "client",
        "Reviewed outreach drafts",
      ),
      row(
        2,
        "supervision",
        de.supervisor.membership_id,
        m.supervision - a,
        3,
        "client",
        "Interview prep check-in",
        weeksBetween(week, today) === 0 ? "pending" : "approved",
      ),
    );
  }
  if (m.setup)
    out.push(
      row(
        3,
        "setup",
        de.supervisor.membership_id,
        m.setup,
        1,
        "client",
        "Onboarding and tool access",
      ),
    );
  if (m.founder)
    out.push(
      row(4, "founder", de.founder_membership_id, m.founder, 2, "agency", "Prompt tuning and QA"),
    );
  return out;
}

// Timeline ------------------------------------------------------------------------------------

const ROLES = [
  "ICU nurse",
  "Staff accountant",
  "Warehouse lead",
  "Java developer",
  "Dental hygienist",
];

export function timeline(de: FakeDe, today: string): TimelineItem[] {
  const out: TimelineItem[] = [];
  const seq = Number.parseInt(de.membership_id.slice(-3), 16) * 65_536;
  const now = Date.now();
  for (let age = Math.min(de.started_weeks_ago, 3); age >= 0; age--) {
    const week = shiftWeek(today, -age);
    const monday = weekStart(week).getTime();
    for (let day = 0; day < 5; day++) {
      const r = noise(`${de.membership_id}:${week}:${day}:tl`);
      const base = monday + day * 86_400_000 + 9 * 3_600_000;
      const n = (3 - age) * 64 + day * 8;
      const push = (
        k: number,
        offsetH: number,
        item: Omit<TimelineItem, "event_id" | "occurred_at">,
      ) => {
        const at = base + offsetH * 3_600_000;
        if (at > now) return;
        out.push({
          event_id: uid(0x7000, seq + n + k),
          occurred_at: new Date(at).toISOString(),
          ...item,
        });
      };
      push(1, 1, { kind: "activity", type: "candidate.sourced", quantity: 5 + Math.round(r * 6) });
      push(2, 3, { kind: "activity", type: "task.completed", quantity: 3 + Math.round(r * 4) });
      if (r > 0.35)
        push(3, 5, {
          kind: "outcome",
          type: "interview.booked",
          quantity: 1,
          subject: { kind: "candidate", id: `cand-${Math.floor(r * 9000) + 1000}` },
          attrs: { role: ROLES[Math.floor(r * ROLES.length)] ?? "Recruiter", stage: "first round" },
        });
      if (r > 0.88)
        push(4, 6, {
          kind: "outcome",
          type: "placement.made",
          quantity: 1,
          value: 450_000,
          subject: { kind: "candidate", id: `cand-${Math.floor(r * 9000) + 1000}` },
          attrs: { role: ROLES[Math.floor(r * ROLES.length)] ?? "Recruiter" },
        });
    }
  }
  return out.sort((a, b) => b.occurred_at.localeCompare(a.occurred_at));
}

// Friday reports ------------------------------------------------------------------------------

export function reports(company: FakeCompany, today: string): FridayReportRecord[] {
  // This week's draft and eleven sent weeks: more than one page (PAGE.reports), newest first.
  const rows: { week: string; state: FridayReportRecord["state"] }[] = Array.from(
    { length: 12 },
    (_, i) => ({ week: shiftWeek(today, -i), state: i === 0 ? "draft" : "sent" }),
  );
  return rows.map(({ week, state }, i) => {
    const friday = new Date(weekStart(week).getTime() + 4 * 86_400_000 + 16 * 3_600_000);
    const generated = state === "draft" ? null : friday.toISOString();
    return {
      company_id: company.id,
      id: uid(0x8000, Number.parseInt(company.id.slice(-4), 16) * 16 + i),
      week_key: week,
      version: 1,
      state,
      snapshot: null,
      html_object_key: generated ? `reports/${company.slug}/${week}.html` : null,
      pdf_object_key: generated ? `reports/${company.slug}/${week}.pdf` : null,
      recipients: [{ email: `owner@${company.slug}.example`, kind: "member" }],
      supervisor_note: null,
      context_sources: [],
      generated_at: generated,
      frozen_at:
        state === "sent" && generated ? new Date(friday.getTime() + 3_600_000).toISOString() : null,
      sent_at:
        state === "sent" && generated ? new Date(friday.getTime() + 3_600_000).toISOString() : null,
      send_error: null,
      actor_kind: "system",
      actor_membership_id: null,
      created_at: generated ?? friday.toISOString(),
      updated_at: generated ?? friday.toISOString(),
    };
  });
}

// DE payloads ---------------------------------------------------------------------------------

export function deSummary(de: FakeDe, today: string, viewer: Viewer): DeSummary {
  // First: reading the latest deploy advances the twin's deploys, which may set `de.deploy`.
  const readiness = deReadiness(de);
  const started = de.started_on !== undefined ? de.started_on : deStartedOn(de, today);
  const runtime: DeSummary["runtime_ref"] =
    de.runtime !== undefined
      ? de.runtime
      : { system: "hermes", instance_id: de.instance, profile_name: de.display_name.toLowerCase() };
  const alias =
    de.litellm_key_alias !== undefined
      ? de.litellm_key_alias
      : `cos-${de.company}-${de.display_name.toLowerCase()}`;
  return {
    company_id: COMPANY_IDS[de.company],
    membership_id: de.membership_id,
    template_key: de.template_key,
    runtime_ref: viewer.operator ? runtime : null,
    runtime_ref_source: viewer.operator && runtime ? "manual" : null,
    litellm_key_alias: viewer.operator || de.fresh ? alias : null,
    spend_cursor: null,
    provisioning_error: de.provisioning_error ?? null,
    status: de.status,
    pause_reason: de.status === "paused" ? (de.pause_reason ?? "manual") : null,
    started_on: started,
    // M3-01 columns (agent composition, pipeline, egress, concurrency, crew origin).
    lead_profile_id: null,
    pipeline_ref: null,
    egress_allowlist: [],
    max_concurrency: 1,
    hired_from_crew_membership_id: null,
    // DC-01 deploy mirror (ADI-02): Riley runs a confirmed bundle; the others were never deployed.
    template_version_id: de.deploy?.template_version_id ?? null,
    bundle_sha: de.deploy?.bundle_sha ?? null,
    deploy_ref: de.deploy?.deploy_ref ?? null,
    verification_state: de.deploy?.verification_state ?? "unverified",
    last_drift_audit_at: null,
    removed_at: de.removed_at ?? null,
    role: de.profile?.role ?? null,
    goal: de.profile?.goal ?? null,
    backstory: de.profile?.backstory ?? null,
    standing_orders: de.profile?.standing_orders ?? null,
    profile_default: de.profile_default ?? null,
    profile_default_at: de.profile_default ? "2026-10-30T09:00:00.000Z" : null,
    // TEMPLATES-T4: no fake DE was set up from a role (no overlay, never on probation).
    overlay_id: null,
    role_version_id: null,
    role_skill_keys: [],
    probation_started_at: null,
    probation_until: null,
    probation_tasks_required: null,
    probation_tasks_done: 0,
    probation_ended_at: null,
    person_name: viewer.personNames ? (de.person_name ?? null) : null,
    actor_kind: "agency_user",
    actor_membership_id: null,
    created_at: `${started ?? "2026-10-01"}T09:00:00Z`,
    updated_at: `${started ?? "2026-10-01"}T09:00:00Z`,
    display_name: de.display_name,
    seat: { id: de.seat_id, title: de.seat_title },
    readiness,
  };
}

export function deProfile(
  de: FakeDe,
  today: string,
  viewer: Viewer & { manager: boolean },
  scorecard: DeProfile["scorecard"],
): DeProfile {
  return {
    ...deSummary(de, today, viewer),
    supervisor: de.supervisor,
    scorecard,
    keys: viewer.manager ? de.keys : [],
    // DC-05: the pilot override of the current deploy (the unverified badge's reason).
    verification_override: de.deploy?.pilot
      ? { ...de.deploy.pilot, expired: Date.parse(de.deploy.pilot.pilot_until) <= Date.now() }
      : null,
    // WF-FIX: production's shape — `{runtime}` for a self-hosted DE, null otherwise.
    self_hosted: de.self_hosted ? { runtime: de.self_hosted.runtime } : null,
  };
}
